import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { useGetRequest } from '../../hooks/useGetRequest/useGetRequest';
import { getExpedienteEvaluacionDetalleAPI } from '../../consumers/backendApisUrls';
import GenericModal from '../../components/modal/GenericModal';
import './ModalExpediente.css';

const getStatusClass = (estadoActual) => {
  const estado = (estadoActual || '').toLowerCase();
  if (estado.includes('enviado')) return 'badge-warning';
  if (estado.includes('final')) return 'badge-success';
  if (estado.includes('cancel')) return 'badge-danger';
  return 'badge-info';
};

const normalizeComparableText = (value) => (
  (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
);

const unwrapApiPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  let current = payload;
  for (let i = 0; i < 6; i += 1) {
    if (!current || typeof current !== 'object') {
      break;
    }

    if (current.data && typeof current.data === 'object') {
      current = current.data;
      continue;
    }

    if (current.Data && typeof current.Data === 'object') {
      current = current.Data;
      continue;
    }

    const keys = Object.keys(current);
    if (keys.length === 1) {
      const nested = current[keys[0]];
      if (nested && typeof nested === 'object') {
        current = nested;
        continue;
      }
    }

    break;
  }

  return current;
};

const getTypeClass = (tipoEvaluacion) => {
  const tipo = normalizeComparableText(tipoEvaluacion);
  if (tipo.includes('psicologica')) return 'expediente-chip expediente-chip--psi';
  if (tipo.includes('conocimientos')) return 'expediente-chip expediente-chip--con';
  return 'expediente-chip expediente-chip--otros';
};

const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return Number(value).toFixed(2);
};

const formatPostulacionOrdinal = (numero) => {
  const n = Number(numero);
  if (!Number.isFinite(n) || n <= 0) return '-';

  const ordinals = {
    1: '1ra',
    2: '2da',
    3: '3ra',
    4: '4ta',
    5: '5ta',
    6: '6ta',
    7: '7ma',
    8: '8va',
    9: '9na',
    10: '10ma'
  };

  return `${ordinals[n] || `${n}ta`} postulación`;
};

const stripHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/<[^>]*>/g, '').trim();
};

const normalizeEvaluacion = (evaluacion = {}) => ({
  ...evaluacion,
  idPostulacionEvaluacion: evaluacion.idPostulacionEvaluacion ?? null,
  idEvaluacion: evaluacion.idEvaluacion || evaluacion.idPostulacionEvaluacion,
  idSubtipoPruebaPsicologica: evaluacion.idSubtipoPruebaPsicologica ?? null,
  estadoEvaluacion: evaluacion.estadoEvaluacion || evaluacion.estado || '',
  tipoEvaluacion: evaluacion.tipoEvaluacion || evaluacion.tipo || '',
  tiempoResolucion: evaluacion.tiempoResolucion || evaluacion.tiempo || '-',
  notaVigesimal: evaluacion.notaVigesimal ?? evaluacion.nota20 ?? null,
  puntajeTotal: evaluacion.puntajeTotal ?? evaluacion.puntaje ?? null
});

const mapKnowledgeDetailToScoring = (detailData) => {
  if (!detailData || !Array.isArray(detailData.preguntas)) {
    return null;
  }

  const preguntas = detailData.preguntas.map((pregunta) => ({
    numero: pregunta.numero,
    marcada: stripHtml(pregunta.marcada) || '-',
    correcta: stripHtml(pregunta.correcta) || '-',
    esCorrecta: Boolean(pregunta.esCorrecta),
    puntaje: Number(pregunta.puntaje || 0)
  }));

  const totalPreguntas = Number(detailData.totalPreguntas ?? preguntas.length);
  const correctas = Number(detailData.correctas ?? preguntas.filter((p) => p.esCorrecta).length);
  const incorrectas = Number(detailData.incorrectas ?? Math.max(totalPreguntas - correctas, 0));
  const porcentaje = Number(detailData.porcentaje ?? (totalPreguntas > 0 ? (correctas * 100) / totalPreguntas : 0));
  const puntajeTotal = Number(detailData.puntajeTotal ?? preguntas.reduce((acc, item) => acc + Number(item.puntaje || 0), 0));
  const puntajeMaximo = Number(detailData.puntajeMaximo || 0);
  const notaVigesimal = puntajeMaximo > 0 ? (puntajeTotal / puntajeMaximo) * 20 : 0;

  return {
    preguntas,
    totalPreguntas,
    correctas,
    incorrectas,
    porcentaje,
    puntajeTotal,
    notaVigesimal
  };
};

const mapDassDetailToPanel = (detailData) => {
  if (!detailData) return null;

  const preguntas = Array.isArray(detailData.preguntas)
    ? detailData.preguntas.map((item) => ({ numero: item.numero, valor: item.valor }))
    : null;

  const criterioResultados = Array.isArray(detailData.criterioResultados)
    ? detailData.criterioResultados
    : null;

  const rangosDass = detailData.rangosDass?.niveles || null;

  if (!preguntas || !criterioResultados) {
    return null;
  }

  return { preguntas, criterioResultados, rangosDass };
};

const mapSalamancaDetailToPanel = (detailData) => {
  if (!detailData) return null;
  if (!Array.isArray(detailData.preguntas) || !Array.isArray(detailData.grupos)) {
    return null;
  }

  return {
    preguntas: detailData.preguntas,
    grupos: detailData.grupos
  };
};

const normalizeExpedientePayload = (payload, idPostulacion) => {
  const unwrappedPayload = unwrapApiPayload(payload);

  if (!unwrappedPayload || typeof unwrappedPayload !== 'object') {
    return null;
  }

  // Contrato esperado por el modal
  if (unwrappedPayload.persona && unwrappedPayload.postulacion && Array.isArray(unwrappedPayload.evaluaciones)) {
    return {
      ...unwrappedPayload,
      postulacion: {
        ...unwrappedPayload.postulacion,
        idPostulacion: idPostulacion || unwrappedPayload.postulacion.idPostulacion
      },
      evaluaciones: unwrappedPayload.evaluaciones.map((ev) => normalizeEvaluacion(ev))
    };
  }

  // Contrato actual del backend
  const infoPersonal = unwrappedPayload.informacionPersonal || {};
  const infoPostulacion = unwrappedPayload.informacionPostulacion || {};
  const resultados = Array.isArray(unwrappedPayload.resultadosPorEvaluacion) ? unwrappedPayload.resultadosPorEvaluacion : [];

  return {
    persona: {
      nombres: infoPersonal.nombres || infoPersonal.nombreCompleto || '-',
      tipoDocumento: infoPersonal.tipoDocumento || '-',
      numeroDocumento: infoPersonal.numeroDocumento || infoPersonal.documento || '-',
      email: infoPersonal.email || '-',
      emailAdicional: infoPersonal.emailAdicional || infoPersonal.correoAlternativo || '',
      celular: infoPersonal.celular || '',
      fueTrabajadorInnova: Boolean(infoPersonal.fueTrabajadorInnova ?? infoPersonal.trabajadorInnovaVigente)
    },
    postulacion: {
      idPostulacion: idPostulacion || infoPostulacion.idPostulacion,
      numeroPostulacion: infoPostulacion.numeroPostulacion || infoPersonal.numeroPostulacion,
      tipoCargo: infoPostulacion.tipoCargo || '-',
      cargo: infoPostulacion.cargo || '-',
      fechaEnvioPruebas: infoPostulacion.fechaEnvioPruebas || '-',
      fechaRegistro: infoPostulacion.fechaRegistro || '-',
      especialidad: infoPostulacion.especialidad || '-',
      estadoActual: infoPostulacion.estadoActual || '-',
      responsable: infoPostulacion.responsable || '-'
    },
    evaluaciones: resultados.map((ev) => normalizeEvaluacion(ev))
  };
};

const PERSONALIDAD_CRITERIOS = [
  {
    nombre: 'Depresion',
    preguntas: [3, 5, 10, 13, 16, 17, 21],
    rangos: [
      { nombre: 'NORMAL', min: 0, max: 9 },
      { nombre: 'LEVE', min: 10, max: 13 },
      { nombre: 'MODERADO', min: 14, max: 20 },
      { nombre: 'SEVERO', min: 21, max: 27 },
      { nombre: 'EXTREMADAMENTE SEVERO', min: 28, max: Number.POSITIVE_INFINITY }
    ]
  },
  {
    nombre: 'Ansiedad',
    preguntas: [2, 4, 7, 9, 15, 19, 20],
    rangos: [
      { nombre: 'NORMAL', min: 0, max: 7 },
      { nombre: 'LEVE', min: 8, max: 9 },
      { nombre: 'MODERADO', min: 10, max: 14 },
      { nombre: 'SEVERO', min: 15, max: 19 },
      { nombre: 'EXTREMADAMENTE SEVERO', min: 20, max: Number.POSITIVE_INFINITY }
    ]
  },
  {
    nombre: 'Estres',
    preguntas: [1, 6, 8, 11, 12, 14, 18],
    rangos: [
      { nombre: 'NORMAL', min: 0, max: 14 },
      { nombre: 'LEVE', min: 15, max: 18 },
      { nombre: 'MODERADO', min: 19, max: 25 },
      { nombre: 'SEVERO', min: 26, max: 36 },
      { nombre: 'EXTREMADAMENTE SEVERO', min: 37, max: Number.POSITIVE_INFINITY }
    ]
  }
];

const pseudoRandom0to3 = (seed) => {
  const x = Math.sin(seed) * 10000;
  const fractional = x - Math.floor(x);
  return Math.floor(fractional * 4);
};

const buildPersonalityScoring = (evaluacionId) => {
  const preguntas = Array.from({ length: 21 }, (_, index) => {
    const numero = index + 1;
    return {
      numero,
      valor: pseudoRandom0to3((evaluacionId || 1) * (numero + 17))
    };
  });

  // Mock controlado para mostrar los tres niveles solicitados: NORMAL, LEVE y MODERADO.
  // DASS-21: cada categoria usa 7 preguntas y el puntaje final se multiplica por 2.
  const forcedValuesByQuestion = {
    // Depresion -> suma 2, final 4 (NORMAL)
    3: 1, 5: 0, 10: 0, 13: 1, 16: 0, 17: 0, 21: 0,
    // Ansiedad -> suma 4, final 8 (LEVE)
    2: 1, 4: 1, 7: 0, 9: 1, 15: 0, 19: 1, 20: 0,
    // Estres -> suma 10, final 20 (MODERADO)
    1: 2, 6: 1, 8: 2, 11: 1, 12: 1, 14: 2, 18: 1
  };

  const preguntasAjustadas = preguntas.map((pregunta) => (
    Object.prototype.hasOwnProperty.call(forcedValuesByQuestion, pregunta.numero)
      ? { ...pregunta, valor: forcedValuesByQuestion[pregunta.numero] }
      : pregunta
  ));

  const criterioResultados = PERSONALIDAD_CRITERIOS.map((criterio) => {
    const totalBase = criterio.preguntas.reduce((sum, numeroPregunta) => {
      const pregunta = preguntasAjustadas.find((item) => item.numero === numeroPregunta);
      return sum + (pregunta ? pregunta.valor : 0);
    }, 0);

    // Estandarizacion DASS-21 para baremos de DASS-42.
    const total = totalBase * 2;

    const diagnostico = criterio.rangos.find((rango) => total >= rango.min && total <= rango.max)?.nombre || 'SIN CLASIFICACION';

    return {
      ...criterio,
      total,
      diagnostico
    };
  });

  return {
    preguntas: preguntasAjustadas,
    criterioResultados
  };
};

const SALAMANCA_GRUPOS = [
  {
    grupo: 'GRUPO A',
    items: [
      { codigo: 'PAR', nombre: 'Paranoide', preguntas: [1, 12] },
      { codigo: 'ESQ', nombre: 'Esquizoide', preguntas: [2, 13] },
      { codigo: 'EQT', nombre: 'Esquizotipico', preguntas: [3, 14] }
    ]
  },
  {
    grupo: 'GRUPO B',
    items: [
      { codigo: 'HIST', nombre: 'Histrionico', preguntas: [4, 15] },
      { codigo: 'ANT', nombre: 'Antisocial', preguntas: [5, 16] },
      { codigo: 'NAR', nombre: 'Narcisista', preguntas: [6, 17] },
      { codigo: 'IE IMO', nombre: 'Sub tipo impulsivo', preguntas: [7, 18] },
      { codigo: 'IE LIM', nombre: 'Sub tipo limite', preguntas: [8, 19] }
    ]
  },
  {
    grupo: 'GRUPO C',
    items: [
      { codigo: 'ANAN', nombre: 'Anancastico', preguntas: [9, 20] },
      { codigo: 'DEP', nombre: 'Dependiente', preguntas: [10, 21] },
      { codigo: 'ANS', nombre: 'Ansioso', preguntas: [11, 22] }
    ]
  }
];

const getSalamancaDiagnostico = (puntaje) => {
  if (puntaje >= 5) return 'ALTO';
  if (puntaje >= 3) return 'MEDIO';
  return 'BAJO';
};

const buildSalamancaScoring = (evaluacionId) => {
  const preguntas = Array.from({ length: 22 }, (_, index) => {
    const numero = index + 1;
    return {
      numero,
      valor: pseudoRandom0to3((evaluacionId || 1) * (numero + 47))
    };
  });

  // Fuerza un par de criterios en rango ALTO para mostrar ese diagnostico de forma visible.
  const forcedHighByQuestion = {
    1: 3,
    12: 3,
    5: 3,
    16: 2
  };

  const preguntasAjustadas = preguntas.map((pregunta) => (
    Object.prototype.hasOwnProperty.call(forcedHighByQuestion, pregunta.numero)
      ? { ...pregunta, valor: forcedHighByQuestion[pregunta.numero] }
      : pregunta
  ));

  const grupos = SALAMANCA_GRUPOS.map((grupo) => ({
    ...grupo,
    items: grupo.items.map((item) => {
      const puntaje = item.preguntas.reduce((sum, numeroPregunta) => {
        const pregunta = preguntasAjustadas.find((it) => it.numero === numeroPregunta);
        return sum + (pregunta ? pregunta.valor : 0);
      }, 0);

      return {
        ...item,
        puntaje,
        diagnostico: getSalamancaDiagnostico(puntaje)
      };
    })
  }));

  return {
    preguntas: preguntasAjustadas,
    grupos
  };
};

const OPCIONES_CONOCIMIENTO = ['A', 'B', 'C', 'D'];

// Simula una configuracion de puntaje por pregunta: algunas valen mas de 1 punto.
const KNOWLEDGE_QUESTION_WEIGHT_MAP = {
  3: 2,
  5: 2,
  8: 2,
  10: 3,
  14: 2,
  17: 2,
  20: 3
};

const getKnowledgeQuestionWeight = (numeroPregunta) => KNOWLEDGE_QUESTION_WEIGHT_MAP[numeroPregunta] || 1;

const calcularNotaVigesimal = (puntajeObtenido, totalPreguntas) => {
  if (!totalPreguntas || totalPreguntas <= 0) return 0;
  return (puntajeObtenido / totalPreguntas) * 20;
};

const buildKnowledgeScoring = (evaluacionId) => {
  const preguntas = Array.from({ length: 20 }, (_, index) => {
    const numero = index + 1;
    const marcada = OPCIONES_CONOCIMIENTO[pseudoRandom0to3((evaluacionId || 1) * (numero + 91))];
    const correcta = OPCIONES_CONOCIMIENTO[pseudoRandom0to3((evaluacionId || 1) * (numero + 137))];
    const esCorrecta = marcada === correcta;
    const peso = getKnowledgeQuestionWeight(numero);
    return {
      numero,
      marcada,
      correcta,
      esCorrecta,
      peso,
      puntaje: esCorrecta ? peso : 0
    };
  });

  const totalPreguntas = preguntas.length;
  const puntajeMaximo = preguntas.reduce((sum, item) => sum + item.peso, 0);
  const correctas = preguntas.filter((item) => item.esCorrecta).length;
  const incorrectas = preguntas.length - correctas;
  const puntajeTotal = preguntas.reduce((sum, item) => sum + item.puntaje, 0);
  const notaVigesimal = calcularNotaVigesimal(puntajeTotal, puntajeMaximo);
  const porcentaje = Math.round((correctas / totalPreguntas) * 100);

  return {
    preguntas,
    totalPreguntas,
    puntajeMaximo,
    correctas,
    incorrectas,
    porcentaje,
    puntajeTotal,
    notaVigesimal
  };
};

const IndicadoresChart = ({ indicadores }) => {
  const indicadoresList = Array.isArray(indicadores) ? indicadores : [];
  if (indicadoresList.length === 0) {
    return <small>Sin indicadores para mostrar.</small>;
  }

  const max = Math.max(...indicadoresList.map((item) => Number(item?.valor || 0)), 100);

  return (
    <div className="expediente-chart-grid">
      {indicadoresList.map((item, index) => {
        const value = Number(item?.valor || 0);
        const width = Math.round((value / max) * 100);
        return (
          <div className="expediente-bar-row" key={item?.criterio || `indicador-${index}`}>
            <div className="expediente-bar-label">{item?.criterio || `Indicador ${index + 1}`}</div>
            <div className="expediente-bar-track">
              <div className="expediente-bar-fill" style={{ width: `${width}%` }} />
            </div>
            <div className="expediente-bar-value">{value}</div>
          </div>
        );
      })}
    </div>
  );
};

const PersonalityScoringPanel = ({ evaluacionId, detailData, enableMockFallback = false }) => {
  const panelData = useMemo(() => {
    const lazyData = mapDassDetailToPanel(detailData);
    if (lazyData) return lazyData;
    if (enableMockFallback) return buildPersonalityScoring(evaluacionId);
    return null;
  }, [evaluacionId, detailData, enableMockFallback]);

  if (!panelData) {
    return <small>Sin detalle para esta evaluacion.</small>;
  }

  const { preguntas, criterioResultados, rangosDass } = panelData;

  const getRangoColor = (nombre) => {
    const key = (nombre || '').toLowerCase();
    if (key.includes('normal')) return 'expediente-rango-normal';
    if (key.includes('leve')) return 'expediente-rango-leve';
    if (key.includes('moderado')) return 'expediente-rango-moderado';
    if (key.includes('extremad')) return 'expediente-rango-extremo';
    return 'expediente-rango-severo';
  };

  return (
    <div className="expediente-personalidad-wrap">
      <div className="expediente-personalidad-header">
        <strong>Puntajes por pregunta (escala 0 a 3)</strong>
      </div>

      <div className="expediente-personalidad-grid">
        <div className="expediente-card-lite">
          <table className="table table-sm mb-0 expediente-mini-table">
            <thead>
              <tr>
                <th className="text-center">Pregunta</th>
                <th className="text-center">Valor</th>
               </tr>
             </thead>
             <tbody>
              {preguntas.map((pregunta) => (
                <tr key={pregunta.numero}>
                  <td className="text-center">P{pregunta.numero}</td>
                  <td className="text-center font-weight-bold">{pregunta.valor}</td>
                </tr>
              ))}
             </tbody>
           </table>
        </div>

        <div className="expediente-card-lite">
          <table className="table table-sm mb-0 expediente-criterio-table">
            <thead>
              <tr>
                <th>Criterio</th>
                <th className="text-center">Preguntas</th>
                <th className="text-center">Total</th>
                <th className="text-center">Resultado</th>
              </tr>
            </thead>
            <tbody>
              {criterioResultados.map((criterio) => (
                <tr key={criterio.nombre}>
                  <td className="font-weight-bold">{criterio.nombre}</td>
                  <td className="text-center">{criterio.preguntas.map((item) => `P${item}`).join(', ')}</td>
                  <td className="text-center font-weight-bold">{criterio.total}</td>
                  <td className="text-center">
                    <span className={`expediente-rango-badge ${getRangoColor(criterio.diagnostico)}`}>{criterio.diagnostico}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="expediente-rangos-box">
            <table className="table table-sm mb-0 expediente-rangos-table">
              <thead>
                <tr>
                  <th>Nivel</th>
                  {PERSONALIDAD_CRITERIOS.map((criterio) => (
                    <th key={criterio.nombre} className="text-center">{criterio.nombre}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(rangosDass) ? rangosDass : ['NORMAL', 'LEVE', 'MODERADO', 'SEVERO', 'EXTREMADAMENTE SEVERO']).map((nivel) => {
                  if (typeof nivel === 'string') {
                    return (
                      <tr key={nivel}>
                        <td className={`font-weight-bold ${getRangoColor(nivel)}`}>{nivel}</td>
                        {PERSONALIDAD_CRITERIOS.map((criterio) => {
                          const rango = criterio.rangos.find((item) => item.nombre === nivel);
                          if (!rango) return <td key={`${criterio.nombre}-${nivel}`} className="text-center">-</td>;
                          const text = Number.isFinite(rango.max) ? `${rango.min} - ${rango.max}` : `${rango.min}+`;
                          return <td key={`${criterio.nombre}-${nivel}`} className="text-center">{text}</td>;
                        })}
                      </tr>
                    );
                  }

                  return (
                    <tr key={nivel.nivel}>
                      <td className={`font-weight-bold ${getRangoColor(nivel.nivel)}`}>{nivel.nivel}</td>
                      <td className="text-center">{nivel.depresion || '-'}</td>
                      <td className="text-center">{nivel.ansiedad || '-'}</td>
                      <td className="text-center">{nivel.estres || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const SalamancaScoringPanel = ({ evaluacionId, detailData, enableMockFallback = false }) => {
  const { preguntas, grupos } = useMemo(() => {
    const lazyData = mapSalamancaDetailToPanel(detailData);
    if (lazyData) return lazyData;
    if (enableMockFallback) return buildSalamancaScoring(evaluacionId);
    return { preguntas: null, grupos: null };
  }, [evaluacionId, detailData, enableMockFallback]);

  if (!preguntas || !grupos) {
    return <small>Sin detalle para esta evaluacion.</small>;
  }

  const getDiagClass = (diagnostico) => {
    if (diagnostico === 'ALTO') return 'expediente-salamanca-alto';
    if (diagnostico === 'MEDIO') return 'expediente-salamanca-medio';
    return 'expediente-salamanca-bajo';
  };

  return (
    <div className="expediente-personalidad-wrap">
      <div className="expediente-personalidad-header">
        <strong>Guia de puntuacion:</strong> 0 Nunca, 1 A veces, 2 Con frecuencia, 3 Siempre
      </div>

      <div className="expediente-personalidad-grid">
        <div className="expediente-card-lite">
          <table className="table table-sm mb-0 expediente-mini-table">
            <thead>
              <tr>
                <th className="text-center">N</th>
                <th className="text-center">Respuesta</th>
              </tr>
            </thead>
            <tbody>
              {preguntas.map((pregunta) => (
                <tr key={pregunta.numero}>
                  <td className="text-center">{pregunta.numero}</td>
                  <td className="text-center font-weight-bold">{pregunta.valor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <div className="expediente-card-lite">
            <table className="table table-sm mb-0 expediente-salamanca-table">
              <thead>
                <tr>
                  <th>Grupo</th>
                  <th>Codigo</th>
                  <th>Criterio</th>
                  <th className="text-center">Puntaje</th>
                  <th className="text-center">Diagnostico</th>
                </tr>
              </thead>
              <tbody>
                {grupos.map((grupo) =>
                  grupo.items.map((item, index) => (
                    <tr key={`${grupo.grupo}-${item.codigo}`}>
                      <td className="font-weight-bold">{index === 0 ? grupo.grupo : ''}</td>
                      <td className="font-weight-bold">{item.codigo}</td>
                      <td>{item.nombre}</td>
                      <td className="text-center font-weight-bold">{item.puntaje}</td>
                      <td className="text-center">
                        <span className={`expediente-rango-badge ${getDiagClass(item.diagnostico)}`}>{item.diagnostico}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="expediente-rangos-box">
            <table className="table table-sm mb-0 expediente-rangos-table">
              <thead>
                <tr>
                  <th>Diagnostico</th>
                  <th className="text-center">Puntuacion</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="font-weight-bold expediente-salamanca-alto">ALTO</td>
                  <td className="text-center font-weight-bold">De 5 a 6</td>
                </tr>
                <tr>
                  <td className="font-weight-bold expediente-salamanca-medio">MEDIO</td>
                  <td className="text-center font-weight-bold">De 3 a 4</td>
                </tr>
                <tr>
                  <td className="font-weight-bold expediente-salamanca-bajo">BAJO</td>
                  <td className="text-center font-weight-bold">De 0 a 2</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const KnowledgeScoringPanel = ({ evaluacionId, scoringData, enableMockFallback = false }) => {
  const panelData = useMemo(
    () => scoringData || (enableMockFallback ? buildKnowledgeScoring(evaluacionId) : null),
    [evaluacionId, scoringData, enableMockFallback]
  );

  if (!panelData) {
    return <small>Sin detalle para esta evaluacion.</small>;
  }

  const { preguntas, totalPreguntas, correctas, incorrectas, porcentaje } = panelData;

  return (
    <div className="expediente-personalidad-wrap">
      <div className="expediente-personalidad-header">
        <strong>Detalle de respuestas ({totalPreguntas} preguntas)</strong>
      </div>

      <div className="expediente-conocimiento-summary">
        <div className="expediente-kpi-box">
          <small>Respuestas correctas</small>
          <strong className="text-success">{correctas}</strong>
        </div>
        <div className="expediente-kpi-box">
          <small>Respuestas incorrectas</small>
          <strong className="text-danger">{incorrectas}</strong>
        </div>
        <div className="expediente-kpi-box">
          <small>Precision</small>
          <strong>{porcentaje}%</strong>
        </div>
      </div>

      <div className="expediente-conocimiento-chart" aria-label="Grafico de rendimiento">
        <div className="expediente-conocimiento-track">
          <div className="expediente-conocimiento-fill expediente-conocimiento-fill--ok" style={{ width: `${porcentaje}%` }} />
          <div className="expediente-conocimiento-fill expediente-conocimiento-fill--bad" style={{ width: `${100 - porcentaje}%` }} />
        </div>
        <div className="expediente-conocimiento-legend">
          <span><i className="expediente-dot expediente-dot--ok" /> Correctas ({correctas})</span>
          <span><i className="expediente-dot expediente-dot--bad" /> Incorrectas ({incorrectas})</span>
        </div>
      </div>

      <div className="expediente-card-lite mt-2">
        <div className="table-responsive">
          <table className="table table-sm mb-0 expediente-conocimiento-table">
            <thead>
              <tr>
                <th className="text-center">Pregunta</th>
                <th className="text-center">Opcion marcada</th>
                <th className="text-center">Puntaje</th>
                <th className="text-center">Resultado</th>
                <th className="text-center">Respuesta correcta</th>
              </tr>
            </thead>
            <tbody>
              {preguntas.map((pregunta) => (
                <tr key={pregunta.numero}>
                  <td className="text-center">P{pregunta.numero}</td>
                  <td className="text-center font-weight-bold">{pregunta.marcada}</td>
                  <td className="text-center font-weight-bold">{pregunta.puntaje}</td>
                  <td className="text-center">
                    <span className={`expediente-rango-badge ${pregunta.esCorrecta ? 'expediente-salamanca-bajo' : 'expediente-salamanca-alto'}`}>
                      {pregunta.esCorrecta ? 'Correcta' : 'Incorrecta'}
                    </span>
                  </td>
                  <td className="text-center">{pregunta.correcta}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ModalExpediente = ({ idPostulacion, onClose, isOpen, expedientePayload, isLoadingExpediente = false }) => {
  const [executeGetRequest] = useGetRequest();
  const [expandedEvaluationId, setExpandedEvaluationId] = useState(null);
  const [detailByPostEvalId, setDetailByPostEvalId] = useState({});
  const [loadingDetailByPostEvalId, setLoadingDetailByPostEvalId] = useState({});

  const expediente = useMemo(() => {
    const payloadNormalizado = normalizeExpedientePayload(expedientePayload, idPostulacion);
    return payloadNormalizado;
  }, [idPostulacion, expedientePayload]);

  if (!isOpen) return null;

  const handleToggleEvaluation = async (evaluacion) => {
    const postEvalId = evaluacion?.idPostulacionEvaluacion || evaluacion?.idEvaluacion;
    if (!postEvalId) return;

    if (expandedEvaluationId === postEvalId) {
      setExpandedEvaluationId(null);
      return;
    }

    setExpandedEvaluationId(postEvalId);

    // Si no hay datos suficientes para lazy-detail, no intentamos llamada.
    if (!expedientePayload || !evaluacion?.idPostulacionEvaluacion || !idPostulacion) {
      return;
    }

    if (detailByPostEvalId[postEvalId] || loadingDetailByPostEvalId[postEvalId]) {
      return;
    }

    setLoadingDetailByPostEvalId((prev) => ({ ...prev, [postEvalId]: true }));
    try {
      const response = await executeGetRequest(
        getExpedienteEvaluacionDetalleAPI(idPostulacion, evaluacion.idPostulacionEvaluacion)
      );

      if (response && response.status === 200 && response.data) {
        const detailPayload = unwrapApiPayload(response.data);
        setDetailByPostEvalId((prev) => ({
          ...prev,
          [postEvalId]: detailPayload
        }));
      }
    } catch (error) {
      // Si falla el lazy detalle, mantenemos render mock local para no romper UX.
    } finally {
      setLoadingDetailByPostEvalId((prev) => ({ ...prev, [postEvalId]: false }));
    }
  };

  // El marco es GenericModal, como el resto de los modales del front: asi el header
  // (barra azul + ícono de cerrar) y los bordes son los mismos en toda la Intranet.
  //
  // El overlay propio se conserva porque este componente se usa suelto —TableWebBody
  // lo renderiza sin ModalEnabled—, asi que tiene que traer su propio fondo y
  // centrado. Su z-index queda por encima del de ModalEnabled (9999) para que siga
  // funcionando tambien cuando lo abre otro modal, como en Traslados y Promociones:
  // al salir por portal a document.body no es hijo de ese wrapper y, con un z-index
  // menor, quedaba visible pero tapado y sin recibir clics.
  return ReactDOM.createPortal(
    <div className="expediente-overlay">
      <GenericModal showModal={onClose} titulo="Expediente Completo" width="1200px">
        <div className="expediente-modal-body">
            {isLoadingExpediente ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Cargando...</span>
                </div>
                <div className="mt-2">Cargando expediente...</div>
              </div>
            ) : null}

            {!isLoadingExpediente && !expediente ? (
              <div className="alert alert-warning mb-0" role="alert">
                No se pudo cargar el expediente en este momento.
              </div>
            ) : null}

            {!isLoadingExpediente && expediente ? (
              <>
            <div className="card mb-4 expediente-card">
              <div className="card-header bg-light expediente-card-header">
                <h6 className="mb-0"><strong>Información Personal</strong></h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3">
                    <p className="mb-1"><strong>Nombre:</strong></p>
                    <p className="mb-3">{expediente.persona.nombres}</p>
                  </div>
                  <div className="col-md-3">
                    <p className="mb-1"><strong>Documento:</strong></p>
                    <p className="mb-3">
                      {expediente.persona.tipoDocumento && expediente.persona.tipoDocumento !== '-'
                        ? `${expediente.persona.tipoDocumento} - ${expediente.persona.numeroDocumento || '-'}`
                        : (expediente.persona.numeroDocumento || '-')}
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p className="mb-1"><strong>Email:</strong></p>
                    <p className="mb-3">{expediente.persona.email}</p>
                  </div>
                  <div className="col-md-3">
                    <p className="mb-1"><strong>Celular:</strong></p>
                    <p className="mb-3">{expediente.persona.celular || '-'}</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-3">
                    <p className="mb-1"><strong>Correo Alternativo:</strong></p>
                    <p className="mb-3">{expediente.persona.emailAdicional || '-'}</p>
                  </div>
                  <div className="col-md-3">
                    <p className="mb-1"><strong>Trabajador Innova:</strong></p>
                    <p className="mb-3">
                      <span className={`badge ${expediente.persona.fueTrabajadorInnova ? 'badge-success' : 'badge-secondary'}`}>
                        {expediente.persona.fueTrabajadorInnova ? 'Si' : 'No'}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-3">
                    <p className="mb-1"><strong>Número de Postulación:</strong></p>
                    <p className="mb-3">{formatPostulacionOrdinal(expediente.postulacion.numeroPostulacion)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4 expediente-card">
              <div className="card-header bg-light expediente-card-header">
                <h6 className="mb-0"><strong>Información de Postulación</strong></h6>
              </div>
              <div className="card-body">
                <div className="expediente-postulacion-grid">
                  <div className="expediente-postulacion-item">
                    <p className="expediente-postulacion-label">Tipo Cargo</p>
                    <p className="expediente-postulacion-value">{expediente.postulacion.tipoCargo || '-'}</p>
                  </div>
                  <div className="expediente-postulacion-item">
                    <p className="expediente-postulacion-label">Cargo al que aplica</p>
                    <p className="expediente-postulacion-value">{expediente.postulacion.cargo}</p>
                  </div>
                  <div className="expediente-postulacion-item">
                    <p className="expediente-postulacion-label">Fecha de Registro</p>
                    <p className="expediente-postulacion-value">{expediente.postulacion.fechaRegistro}</p>
                  </div>
                  <div className="expediente-postulacion-item">
                    <p className="expediente-postulacion-label">Estado Actual</p>
                    <p className="expediente-postulacion-value"><span className={`badge ${getStatusClass(expediente.postulacion.estadoActual)}`}>{expediente.postulacion.estadoActual}</span></p>
                  </div>
                  <div className="expediente-postulacion-item">
                    <p className="expediente-postulacion-label">Especialidad</p>
                    <p className="expediente-postulacion-value">{expediente.postulacion.especialidad}</p>
                  </div>
                  <div className="expediente-postulacion-item expediente-postulacion-item--wide">
                    <p className="expediente-postulacion-label">Responsable</p>
                    <p className="expediente-postulacion-value">{expediente.postulacion.responsable}</p>
                  </div>
                  <div className="expediente-postulacion-item">
                    <p className="expediente-postulacion-label">Fecha Envío de Pruebas</p>
                    <p className="expediente-postulacion-value">{expediente.postulacion.fechaEnvioPruebas}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4 expediente-card">
              <div className="card-header bg-light expediente-card-header">
                <h6 className="mb-0"><strong>Resultados por Evaluación ({expediente.evaluaciones.length})</strong></h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0 expediente-results-table">
                    <thead className="table-light">
                      <tr>
                        <th className="text-center">Fecha Resolución</th>
                        <th>Nombre de Evaluación</th>
                        <th className="text-center">Tipo</th>
                        <th className="text-center">Subtipo</th>
                        <th className="text-center">Tiempo</th>
                        <th className="text-center">Estado</th>
                        <th className="text-center">Nota / 20</th>
                        <th className="text-center">Gráfica</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...expediente.evaluaciones]
                        .sort((a, b) => {
                          const ordenEstado = (ev) => {
                            const estado = (ev.estadoEvaluacion || '').toLowerCase();
                            if (estado.includes('complet') || estado.includes('finaliz')) return 0;
                            return 1;
                          };
                          return ordenEstado(a) - ordenEstado(b);
                        })
                        .map((evaluacion) => {
                        const rowId = evaluacion.idPostulacionEvaluacion || evaluacion.idEvaluacion;
                        const detailResponse = detailByPostEvalId[rowId];
                        const detailData = detailResponse?.detalle;
                        const isDetailLoading = Boolean(loadingDetailByPostEvalId[rowId]);
                        const isExpanded = expandedEvaluationId === rowId;
                        const tipoEvaluacionNormalizado = normalizeComparableText(evaluacion.tipoEvaluacion);
                        const isConocimientos = tipoEvaluacionNormalizado.includes('conocimientos');
                        const isOtros = tipoEvaluacionNormalizado.includes('otros');
                        const isPsicologica = tipoEvaluacionNormalizado.includes('psicologica');
                        const subtipoId = Number(evaluacion.idSubtipoPruebaPsicologica);
                        const subtipoNormalizado = normalizeComparableText(evaluacion.subtipo);
                        const isDass = subtipoId === 1 || subtipoNormalizado.includes('dass');
                        const isSalamanca = subtipoId === 2 || subtipoNormalizado.includes('salamanca');
                        const estadoEvaluacion = evaluacion.estadoEvaluacion || 'Completada';
                        const isPendiente = estadoEvaluacion.toLowerCase() === 'pendiente';
                        const shouldUseLazyDetail = Boolean(expedientePayload && evaluacion.idPostulacionEvaluacion && idPostulacion);
                        const knowledgeScoring = (isConocimientos || isOtros)
                          ? (mapKnowledgeDetailToScoring(detailData) || (!shouldUseLazyDetail ? buildKnowledgeScoring(evaluacion.idEvaluacion) : null))
                          : null;
                        const indicadoresData = detailData?.indicadores || (!shouldUseLazyDetail ? evaluacion.indicadores : null);
                        const notaVigesimalMostrada = knowledgeScoring ? knowledgeScoring.notaVigesimal : evaluacion.notaVigesimal;
                        return (
                          <React.Fragment key={rowId}>
                            <tr>
                              <td className="text-center small">{evaluacion.fechaResolucion || evaluacion.fechaEnvioPruebas || '-'}</td>
                              <td className="small font-weight-bold">{evaluacion.nombreEvaluacion}</td>
                              <td className="text-center small"><span className={getTypeClass(evaluacion.tipoEvaluacion)}>{evaluacion.tipoEvaluacion}</span></td>
                              <td className="text-center small">{isPsicologica ? (evaluacion.subtipo || '-') : '-'}</td>
                              <td className="text-center small">{evaluacion.tiempoResolucion}</td>
                              <td className="text-center small">{estadoEvaluacion ? estadoEvaluacion.charAt(0).toUpperCase() + estadoEvaluacion.slice(1).toLowerCase() : ''}</td>
                              <td className={`text-center small font-weight-bold ${(!isPsicologica && !isPendiente) ? 'text-success' : ''}`}>{(isPsicologica || isPendiente) ? '-' : formatNumber(notaVigesimalMostrada)}</td>
                              <td className="text-center small">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary expediente-chart-button"
                                  onClick={() => handleToggleEvaluation(evaluacion)}
                                  title="Ver gráfica de criterios"
                                >
                                  <FontAwesomeIcon icon={faChartBar} className="mr-1" />
                                  {isExpanded ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />}
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="expediente-expanded-row">
                                <td colSpan="8">
                                  <div className="expediente-expanded-content">
                                    <div className="expediente-expanded-header">
                                      <h6>{evaluacion.nombreEvaluacion}</h6>
                                      <small>
                                        Criterios analizados
                                        {evaluacion.subtipo ? ` (${evaluacion.tipoEvaluacion} - ${evaluacion.subtipo})` : ` (${evaluacion.tipoEvaluacion})`}
                                      </small>
                                    </div>
                                    {isDetailLoading ? (
                                      <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                          <span className="sr-only">Cargando...</span>
                                        </div>
                                        <div className="mt-2">Cargando expediente...</div>
                                      </div>
                                    ) : (
                                      <>
                                        {(isConocimientos || isOtros) ? (
                                          <KnowledgeScoringPanel
                                            evaluacionId={evaluacion.idEvaluacion}
                                            scoringData={knowledgeScoring}
                                            enableMockFallback={!shouldUseLazyDetail}
                                          />
                                        ) : null}
                                        {isPsicologica && isDass ? (
                                            <PersonalityScoringPanel
                                              evaluacionId={evaluacion.idEvaluacion}
                                              detailData={detailData}
                                              enableMockFallback={!shouldUseLazyDetail}
                                            />
                                          ) : null}
                                        {isPsicologica && isSalamanca ? (
                                            <SalamancaScoringPanel
                                              evaluacionId={evaluacion.idEvaluacion}
                                              detailData={detailData}
                                              enableMockFallback={!shouldUseLazyDetail}
                                            />
                                          ) : null}
                                        {!isConocimientos && !isOtros && !(isPsicologica && (isDass || isSalamanca)) ? (
                                            <IndicadoresChart indicadores={indicadoresData} />
                                          ) : null}
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
              </>
            ) : null}
          </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </GenericModal>
    </div>,
    document.body
  );
};

export default ModalExpediente;
